import React, { useState, useEffect, useRef } from 'react';
import './RangeInput.css';
import browserIsFirefox from '../../common/isFirefox';
import logComponent from '../../common/logComponent';
import GraphicalIcon from '../GraphicalIcon/GraphicalIcon';

export default function RangeInput({ initial, min, max, startAtNull, onChange, onBlur, showNumbers, showLabels, showGraphics, items, alwaysShowOptions }) {
  const [dragging, setDragging] = useState(false);
  const [internalValue, setInternalValue] = useState(initial ? initial : min);
  const [externalValue, setExternalValue] = useState(startAtNull ? null : initial ? initial : min);
  const [handleLeft, setHandleLeft] = useState(null);
  const [selectedItem, setSelectedItem] = useState(items.find(item => item.value == initial ? initial : min) ?? null);
  const [externalLabelValue, setExternalLabelValue] = useState(items.find(item => item.value == initial ? initial : min).key ?? null);

  const [needsOnChange, setNeedsOnChange] = useState(false);

  const sliderTrackRef = useRef(null);
  const sliderHandleRef = useRef(null);

  const setSliderTrackRef = el => sliderTrackRef.current = el;
  const setSliderHandleRef = el => sliderHandleRef.current = el;



  if (logComponent('RangeInput')) { console.debug('RangeInput rendered'); }


  useEffect(() => {
    if (logComponent('RangeInput')) { console.debug('RangeInput onMount'); }
    moveHandle();
    attachResizeListener();
    return () => {
      removeResizeListener();
    };
  }, [])

  useEffect(() => {
    if (logComponent('RangeInput')) { console.debug('RangeInput useEffect [internalValue]'); }
    moveHandle();
    updateSelectedItem();
    updateExternalValue();
  }, [internalValue])

  useEffect(() => {
    if (logComponent('RangeInput')) { console.debug('RangeInput useEffect [needsOnChange]'); }
    if (needsOnChange === true) {
      if (onChange) onChange(externalValue);
      setNeedsOnChange(false);
    }
  }, [needsOnChange])

  const attachResizeListener = () => {
    if (logComponent('RangeInput')) { console.debug('RangeInput attachResizeListener'); }
    window.addEventListener('resize', moveHandle);
  }

  const removeResizeListener = () => {
    if (logComponent('RangeInput')) { console.debug('RangeInput removeResizeListener'); }
    window.removeEventListener('resize', moveHandle);
  }

  const updateExternalValue = () => {
    if (logComponent('RangeInput')) { console.debug('RangeInput updateExternalValue'); }
    setExternalValue(internalValue);

    let label = items.find(item => item.value == internalValue).key;
    setExternalLabelValue(label ?? null);
    setNeedsOnChange(true);
  }

  const updateSelectedItem = () => {
    if (logComponent('RangeInput')) { console.debug('RangeInput updateSelectedItem'); }
    setSelectedItem(items.find(item => item.value == internalValue) ?? null);
    setNeedsOnChange(true);
  }

  const moveHandle = () => {
    if (logComponent('RangeInput')) { console.debug('RangeInput moveHandle'); }
    const ratio = (internalValue - min) / (max - min);
    const trackLength = sliderTrackRef.current.clientWidth;
    const handleWidth = sliderHandleRef.current.clientWidth;

    setHandleLeft(ratio * (trackLength - handleWidth));
  }

  const handleMouseDown = (e) => {
    if (logComponent('RangeInput')) { console.debug('RangeInput handleMouseDown'); }
    setDragging(true);

    if (browserIsFirefox()) e.target.setPointerCapture(e.pointerId);
  }

  const handleMouseMove = (e) => {
    if (logComponent('RangeInput')) { console.debug('RangeInput handleMouseMove'); }
    if (!dragging) return;

    const handleWidth = sliderHandleRef.current.clientWidth;
    const trackLeft = sliderTrackRef.current.offsetLeft;
    const trackLength = sliderTrackRef.current.clientWidth;

    // Handle both mouse and touch events
    const mouseX = e.touches ? e.touches[0].pageX : e.pageX;

    let newInternalValue = Math.round((mouseX - trackLeft - (handleWidth / 2)) / (trackLength - handleWidth) * (max - min)) + min;

    if (newInternalValue < min) {
      newInternalValue = min;
    } else if (newInternalValue > max) {
      newInternalValue = max;
    }
    if (items) {
      if (items.length > newInternalValue - 1)
        newInternalValue = items[newInternalValue - 1].value;
    }
    if (newInternalValue !== internalValue) {
      setInternalValue(newInternalValue);
    }
  }

  const handleMouseUp = (e) => {
    if (logComponent('RangeInput')) { console.debug('RangeInput handleMouseUp'); }
    updateSelectedItem();
    updateExternalValue();
    setDragging(false);

    sliderHandleRef.current.blur();
    onBlur(externalValue);

    if (browserIsFirefox()) e.target.releasePointerCapture(e.pointerId);
  }

  const handleTouchStart = (e) => {
    if (logComponent('RangeInput')) { console.debug('RangeInput handleTouchStart'); }
    handleMouseDown(e);
  }

  const handleTouchMove = (e) => {
    if (logComponent('RangeInput')) { console.debug('RangeInput handleTouchMove'); }
    handleMouseMove(e);
  }

  const handleTouchEnd = (e) => {
    if (logComponent('RangeInput')) { console.debug('RangeInput handleTouchEnd'); }
    handleMouseUp(e);
  }
  return (
    <div
      className="Range-Input">
      <div
        className={'Range-Input__backdrop' + (dragging ? ' Range-Input__backdrop--visible' : '')}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
        onTouchMove={handleMouseMove}
      ></div>
      <div
        className="Range-Input__options">
        <div
          className="Range-Input__options-items">
          {items &&
            items.map(item =>
              <div className="Range-Input__options-item" key={item.value.toString()}>
                {alwaysShowOptions &&
                  <div>
                    {
                      showGraphics && showGraphics === true &&
                      <div className="Range-Input__options-icon">
                        <GraphicalIcon url={item.iconUrl} svg={item.iconSvg} alt={`Slider Option Icon_${item.value.toString()}`} />
                      </div>
                    }
                    {
                      showNumbers && showNumbers === true &&
                      <div className="Range-Input__options-number">
                        {item.value}
                      </div>
                    }
                  </div>}
              </div>
            )
          }
        </div>
        <div
          className="Range-Input__options-label">
          {items && alwaysShowOptions && showLabels &&
            <span>{externalLabelValue}</span>
          }
        </div>
      </div>
      <div className="Range-Input__slider">
        {
          !showNumbers &&
          <div className="Range-Input__slider-min">{min}</div>
        }
        <div
          className="Range-Input__slider-track"
          ref={setSliderTrackRef}>
          <div
            className="Range-Input__slider-handle-wrapper"
            style={{ left: handleLeft }}>
            {!alwaysShowOptions &&
              <div>
                {
                  showGraphics && showGraphics === true &&
                  <div className="Range-Input__icon">
                    {selectedItem &&
                      <GraphicalIcon svg={selectedItem.iconSvg} url={selectedItem.iconUrl} alt={`Slider Option Icon_${selectedItem.value.toString()}`} />
                    }
                  </div>
                }
                <div className="Range-Input__floating-value">{externalValue}</div>
              </div>
            }
            <div
              className={'Range-Input__slider-handle' + (dragging ? ' Range-Input__slider-handle--dragging' : '')}
              tabIndex={0}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseMove={dragging ? handleMouseMove : null}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
              ref={setSliderHandleRef}>
            </div>
          </div>
        </div>
        {
          !showNumbers &&
          <div className="Range-Input__slider-max">{max}</div>
        }
      </div>
    </div>
  );
}